// src/pages/teacher/TeacherMessages.tsx
// הודעות — צפייה בהודעות שנשלחו מהגן + שליחת הודעות להורים

import { useState, useEffect, useMemo } from 'react';
import { MessageSquare, Send, ChevronDown, ChevronUp, Users, Clock, CheckCheck, Loader2, RefreshCw, Plus, X, ArrowRight, FileText, User, Search } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useMessageStore, type TemplateType, type MessageTemplate } from '../../stores/messageStore';
import api from '../../services/api';
import { teacherColors as colors } from '../../styles/colors';

// API response shape from backend
interface ApiMessage {
  publicId: string;
  content: string;
  messageType: string; // "General" | "ChildSpecific"
  childPublicId?: string;
  childName?: string;
  sentAt: string;
  recipients?: { parentName: string; isRead: boolean; readAt?: string | null }[];
}

// Mapped UI shape
interface KGMessage {
  publicId: string;
  title: string;
  body: string;
  isGeneral: boolean;
  senderName: string;
  recipientCount: number;
  readCount: number;
  createdAt: string;
  childName?: string;
  recipients?: { parentName: string; childName: string; isRead: boolean }[];
}

// Simple child type for compose
interface SimpleChild {
  publicId: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
}

type ComposeStep = 'closed' | 'choose-type' | 'select-child' | 'compose' | 'confirm' | 'templates';

export default function TeacherMessages() {
  const { user } = useAuthStore();
  const {
    templates,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplatesByType,
    sendMessage,
  } = useMessageStore();

  const [messages, setMessages] = useState<KGMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Compose state
  const [composeStep, setComposeStep] = useState<ComposeStep>('closed');
  const [composeType, setComposeType] = useState<'child' | 'general'>('general');
  const [selectedChild, setSelectedChild] = useState<SimpleChild | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendToMobile, setSendToMobile] = useState(false);
  const [childSearch, setChildSearch] = useState('');

  // Children list (loaded lazily)
  const [children, setChildren] = useState<SimpleChild[]>([]);
  const [loadingChildren, setLoadingChildren] = useState(false);

  // Template editing
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [templateType, setTemplateType] = useState<TemplateType>('child');

  useEffect(() => {
    loadMessages();
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const loadMessages = async () => {
    setLoading(true);
    try {
      const kgPublicId = user?.kindergartenPublicId;
      if (!kgPublicId) { setLoading(false); return; }

      const response = await api.get(`/kindergarten/${kgPublicId}/messages`);
      const raw = response.data?.data || response.data;
      const apiMessages: ApiMessage[] = Array.isArray(raw)
        ? raw
        : raw?.messages && Array.isArray(raw.messages)
          ? raw.messages
          : [];

      const mapped: KGMessage[] = apiMessages.map((m: ApiMessage) => {
        const recipients = m.recipients || [];
        const readCount = recipients.filter(r => r.isRead).length;
        const contentLines = (m.content || '').split('\n');
        const title = contentLines[0]?.substring(0, 80) || 'הודעה';

        return {
          publicId: m.publicId,
          title,
          body: m.content || '',
          isGeneral: m.messageType === 'General',
          senderName: '',
          recipientCount: recipients.length,
          readCount,
          createdAt: m.sentAt,
          childName: m.childName,
          recipients: recipients.map(r => ({
            parentName: r.parentName,
            childName: m.childName || '',
            isRead: r.isRead,
          })),
        };
      });

      setMessages(mapped);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadChildren = async () => {
    if (children.length > 0) return; // already loaded
    const kgPublicId = user?.kindergartenPublicId;
    if (!kgPublicId) return;

    setLoadingChildren(true);
    try {
      // Use sync endpoint to get children list
      const response = await api.post('/sync/full', {
        kindergartenPublicId: kgPublicId,
        syncType: 'Full',
      });
      const data = response.data?.data || response.data;
      const expectedChildren = data?.expectedChildren || [];
      const mapped: SimpleChild[] = expectedChildren.map((c: any) => ({
        publicId: c.publicId,
        firstName: c.firstName || '',
        lastName: c.lastName || '',
        photoUrl: c.photoUrl,
      }));
      setChildren(mapped.sort((a, b) => a.firstName.localeCompare(b.firstName, 'he')));
    } catch (err) {
      console.error('Failed to load children:', err);
    } finally {
      setLoadingChildren(false);
    }
  };

  const filteredChildren = useMemo(() => {
    if (!childSearch.trim()) return children;
    const q = childSearch.trim().toLowerCase();
    return children.filter(c =>
      c.firstName.toLowerCase().includes(q) ||
      c.lastName.toLowerCase().includes(q) ||
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(q)
    );
  }, [children, childSearch]);

  const openCompose = () => {
    setComposeStep('choose-type');
    setSelectedChild(null);
    setSelectedTemplateId(null);
    setMessageText('');
    setSendToMobile(false);
  };

  const closeCompose = () => {
    setComposeStep('closed');
    setSelectedChild(null);
    setSelectedTemplateId(null);
    setMessageText('');
    setSendToMobile(false);
    setChildSearch('');
  };

  const handleChooseType = (type: 'child' | 'general') => {
    setComposeType(type);
    if (type === 'child') {
      loadChildren();
      setComposeStep('select-child');
    } else {
      setComposeStep('compose');
    }
  };

  const handleSelectChild = (child: SimpleChild) => {
    setSelectedChild(child);
    setComposeStep('compose');
  };

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      let text = template.content;
      if (selectedChild) {
        text = text.replace(/{childName}/g, `${selectedChild.firstName} ${selectedChild.lastName}`);
      }
      setMessageText(text);
    }
  };

  const handleSend = async () => {
    const kgPublicId = user?.kindergartenPublicId;
    if (!messageText.trim() || !kgPublicId) return;

    setSending(true);
    const result = await sendMessage({
      childPublicId: selectedChild?.publicId,
      childName: selectedChild ? `${selectedChild.firstName} ${selectedChild.lastName}` : undefined,
      templateId: selectedTemplateId || undefined,
      content: messageText.trim(),
      kindergartenPublicId: kgPublicId,
      sendToMobile,
    });
    setSending(false);

    if (result.success) {
      const smsNote = sendToMobile ? ' + SMS' : '';
      showToast(selectedChild ? `ההודעה נשלחה להורי ${selectedChild.firstName}${smsNote}` : `ההודעה נשלחה לכל ההורים${smsNote}`);
      closeCompose();
      loadMessages(); // refresh list
    } else {
      showToast(result.error || 'שגיאה בשליחה');
    }
  };

  // Template management
  const openTemplates = () => {
    setComposeStep('templates');
    resetTemplateForm();
  };

  const resetTemplateForm = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateContent('');
    setTemplateType('child');
  };

  const handleSaveTemplate = () => {
    if (!templateName.trim() || !templateContent.trim()) return;
    if (editingTemplate) {
      updateTemplate(editingTemplate.id, templateName.trim(), templateContent.trim(), templateType);
      showToast('התבנית עודכנה');
    } else {
      addTemplate(templateName.trim(), templateContent.trim(), templateType);
      showToast('תבנית חדשה נוצרה');
    }
    resetTemplateForm();
  };

  const handleEditTemplate = (t: MessageTemplate) => {
    setEditingTemplate(t);
    setTemplateName(t.name);
    setTemplateContent(t.content);
    setTemplateType(t.type);
  };

  const fmtDate = (s: string) => {
    try {
      const d = new Date(s);
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      const diffHrs = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffMin < 1) return 'עכשיו';
      if (diffMin < 60) return `לפני ${diffMin} דק׳`;
      if (diffHrs < 24) return `לפני ${diffHrs} שעות`;
      if (diffDays < 7) return `לפני ${diffDays} ימים`;
      return d.toLocaleDateString('he-IL');
    } catch { return s; }
  };

  const currentTemplates = composeType === 'child'
    ? getTemplatesByType('child')
    : getTemplatesByType('general');

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', position: 'relative' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: colors.text, margin: 0 }}>הודעות</h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={openTemplates}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '8px 12px', borderRadius: '8px',
              border: `1px solid ${colors.border}`, background: colors.white,
              fontSize: '13px', color: colors.textLight, cursor: 'pointer',
            }}
          >
            <FileText size={14} />
            תבניות
          </button>
          <button onClick={loadMessages} disabled={loading} style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}>
            <RefreshCw size={18} color={colors.textLight} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Send button */}
      <button
        onClick={openCompose}
        style={{
          width: '100%', padding: '14px', marginBottom: '16px',
          borderRadius: '12px', border: 'none',
          background: `linear-gradient(135deg, ${colors.primary}, #a78bfa)`,
          color: 'white', fontSize: '16px', fontWeight: 'bold',
          cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: '8px',
        }}
      >
        <Send size={18} />
        שליחת הודעה חדשה
      </button>

      {/* Messages list */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '32px', color: colors.textLight }}>
          <Loader2 size={24} style={{ margin: '0 auto 8px', animation: 'spin 1s linear infinite' }} />
          <p style={{ fontSize: '14px' }}>טוען הודעות...</p>
        </div>
      )}

      {!loading && messages.length === 0 && (
        <div style={{
          backgroundColor: colors.white, borderRadius: '14px',
          padding: '48px 24px', border: `1px solid ${colors.border}`,
          textAlign: 'center',
        }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '14px',
            backgroundColor: colors.primaryLight,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <MessageSquare size={28} color={colors.primary} />
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
            אין הודעות
          </h3>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            שלחי הודעה חדשה להורים
          </p>
        </div>
      )}

      {!loading && messages.map(msg => {
        const isExpanded = expandedId === msg.publicId;
        const readPct = msg.recipientCount > 0 ? Math.round((msg.readCount / msg.recipientCount) * 100) : 0;

        return (
          <div key={msg.publicId} style={{
            backgroundColor: colors.white, borderRadius: '12px',
            border: `1px solid ${colors.border}`, marginBottom: '10px',
            overflow: 'hidden',
          }}>
            <button
              onClick={() => setExpandedId(isExpanded ? null : msg.publicId)}
              style={{
                width: '100%', padding: '14px 16px',
                backgroundColor: 'transparent', border: 'none',
                cursor: 'pointer', textAlign: 'right',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>{msg.title}</span>
                    {msg.isGeneral ? (
                      <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '4px', backgroundColor: colors.primaryLight, color: colors.primary, fontWeight: '500' }}>כללי</span>
                    ) : msg.childName ? (
                      <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '4px', backgroundColor: colors.greenLight, color: '#166534', fontWeight: '500' }}>{msg.childName}</span>
                    ) : null}
                  </div>
                  <p style={{ fontSize: '12px', color: colors.textLight, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap' }}>
                    {msg.body}
                  </p>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '6px', fontSize: '11px', color: colors.textLight }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Clock size={11} /> {fmtDate(msg.createdAt)}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Users size={11} /> {msg.recipientCount} נמענים
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: readPct === 100 ? colors.green : colors.textLight }}>
                      <CheckCheck size={11} /> {readPct}% נקראו
                    </span>
                  </div>
                </div>
                <div style={{ paddingTop: '2px', marginRight: '8px' }}>
                  {isExpanded ? <ChevronUp size={16} color={colors.textLight} /> : <ChevronDown size={16} color={colors.textLight} />}
                </div>
              </div>
            </button>

            {isExpanded && (
              <div style={{ padding: '0 16px 14px', borderTop: `1px solid ${colors.border}` }}>
                <div style={{ padding: '12px 0', fontSize: '13px', color: colors.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {msg.body}
                </div>
                <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                  <div style={{ height: '4px', backgroundColor: colors.border, borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${readPct}%`, backgroundColor: colors.green, borderRadius: '2px', transition: 'width 0.3s' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: colors.textLight, marginTop: '4px' }}>
                    <span>{msg.readCount} קראו</span>
                    <span>{msg.recipientCount - msg.readCount} לא קראו</span>
                  </div>
                </div>

                {msg.recipients && msg.recipients.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <p style={{ fontSize: '11px', fontWeight: '600', color: colors.textLight, marginBottom: '4px' }}>נמענים:</p>
                    {msg.recipients.map((r, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: colors.text }}>
                        <span>{r.parentName} {r.childName ? `(${r.childName})` : ''}</span>
                        {r.isRead ? (
                          <CheckCheck size={12} color={colors.green} />
                        ) : (
                          <Clock size={12} color={colors.textLight} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* =============================================== */}
      {/* Compose Modal */}
      {/* =============================================== */}
      {composeStep !== 'closed' && (
        <div
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeCompose(); }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '16px',
          }}
        >
          <div dir="rtl" style={{
            background: 'white', borderRadius: '16px',
            width: '100%', maxWidth: '440px', maxHeight: '85vh',
            overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          }}>
            {/* Modal header */}
            <div style={{
              padding: '14px 16px', borderBottom: `1px solid ${colors.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: `linear-gradient(135deg, ${colors.primary}, #a78bfa)`, color: 'white',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {composeStep !== 'choose-type' && composeStep !== 'templates' && (
                  <button
                    onClick={() => {
                      if (composeStep === 'select-child') setComposeStep('choose-type');
                      else if (composeStep === 'compose') {
                        if (composeType === 'child') { setSelectedChild(null); setComposeStep('select-child'); }
                        else setComposeStep('choose-type');
                      }
                      else if (composeStep === 'confirm') setComposeStep('compose');
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.2)', border: 'none',
                      borderRadius: '6px', padding: '4px 8px', color: 'white',
                      cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px',
                    }}
                  >
                    <ArrowRight size={14} /> חזרה
                  </button>
                )}
                <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                  {composeStep === 'choose-type' && 'הודעה חדשה'}
                  {composeStep === 'select-child' && 'בחירת ילד/ה'}
                  {composeStep === 'compose' && (selectedChild ? `הודעה ל${selectedChild.firstName}` : 'הודעה כללית')}
                  {composeStep === 'confirm' && 'אישור שליחה'}
                  {composeStep === 'templates' && 'ניהול תבניות'}
                </span>
              </div>
              <button onClick={closeCompose} style={{
                background: 'rgba(255,255,255,0.2)', border: 'none',
                borderRadius: '50%', width: '30px', height: '30px',
                color: 'white', cursor: 'pointer', fontSize: '14px',
              }}>
                <X size={16} />
              </button>
            </div>

            {/* Modal content */}
            <div style={{ padding: '16px', overflowY: 'auto', maxHeight: 'calc(85vh - 56px)' }}>

              {/* Step 1: Choose type */}
              {composeStep === 'choose-type' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <button
                    onClick={() => handleChooseType('child')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '16px', background: '#f8f9fa',
                      border: '2px solid #e0e0e0', borderRadius: '12px',
                      cursor: 'pointer', textAlign: 'right',
                    }}
                  >
                    <User size={28} color={colors.primary} />
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '2px' }}>הודעה לילד/ה ספציפי/ת</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>בחרי ילד/ה ושלחי הודעה להוריו</div>
                    </div>
                  </button>
                  <button
                    onClick={() => handleChooseType('general')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '16px', background: '#f8f9fa',
                      border: '2px solid #e0e0e0', borderRadius: '12px',
                      cursor: 'pointer', textAlign: 'right',
                    }}
                  >
                    <Users size={28} color={colors.primary} />
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '2px' }}>הודעה לכל ההורים</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>שלחי הודעה כללית לכל הורי הגן</div>
                    </div>
                  </button>
                </div>
              )}

              {/* Step 2: Select child */}
              {composeStep === 'select-child' && (
                <div>
                  <div style={{ position: 'relative', marginBottom: '12px' }}>
                    <Search size={16} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: colors.textLight }} />
                    <input
                      type="text"
                      placeholder="חיפוש ילד/ה..."
                      value={childSearch}
                      onChange={(e) => setChildSearch(e.target.value)}
                      style={{
                        width: '100%', padding: '10px 36px 10px 10px',
                        borderRadius: '10px', border: `2px solid ${colors.border}`,
                        fontSize: '14px', boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {loadingChildren ? (
                    <div style={{ textAlign: 'center', padding: '32px', color: colors.textLight }}>
                      <Loader2 size={20} style={{ margin: '0 auto 8px', animation: 'spin 1s linear infinite' }} />
                      <div style={{ fontSize: '13px' }}>טוען רשימת ילדים...</div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '350px', overflowY: 'auto' }}>
                      {filteredChildren.map(child => (
                        <button
                          key={child.publicId}
                          onClick={() => handleSelectChild(child)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: '10px 12px', background: '#f8f9fa',
                            border: '1px solid #eee', borderRadius: '10px',
                            cursor: 'pointer', textAlign: 'right',
                          }}
                        >
                          <div style={{
                            width: '36px', height: '36px', borderRadius: '50%',
                            background: child.photoUrl ? `url(${child.photoUrl}) center/cover no-repeat` : `linear-gradient(135deg, ${colors.primary}, #a78bfa)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}>
                            {!child.photoUrl && (
                              <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
                                {child.firstName.charAt(0)}
                              </span>
                            )}
                          </div>
                          <span style={{ fontWeight: '600', fontSize: '14px' }}>
                            {child.firstName} {child.lastName}
                          </span>
                        </button>
                      ))}
                      {filteredChildren.length === 0 && !loadingChildren && (
                        <div style={{ textAlign: 'center', padding: '24px', color: colors.textLight, fontSize: '13px' }}>
                          {children.length === 0 ? 'לא נמצאו ילדים בגן' : 'לא נמצאו תוצאות'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Compose */}
              {composeStep === 'compose' && (
                <div>
                  {/* Selected child card */}
                  {selectedChild && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      marginBottom: '16px', padding: '10px',
                      background: colors.greenLight, borderRadius: '10px',
                    }}>
                      <div style={{
                        width: '40px', height: '40px', borderRadius: '50%',
                        background: selectedChild.photoUrl
                          ? `url(${selectedChild.photoUrl}) center/cover no-repeat`
                          : `linear-gradient(135deg, ${colors.primary}, #a78bfa)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        {!selectedChild.photoUrl && (
                          <span style={{ fontSize: '16px', fontWeight: 'bold', color: 'white' }}>
                            {selectedChild.firstName.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                          {selectedChild.firstName} {selectedChild.lastName}
                        </div>
                        <div style={{ fontSize: '11px', color: '#166534' }}>ההודעה תשלח להורי הילד/ה</div>
                      </div>
                    </div>
                  )}

                  {/* Template selector */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '6px', fontSize: '13px', color: colors.text }}>
                      בחרי תבנית:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '80px', overflowY: 'auto' }}>
                      {currentTemplates.map(t => (
                        <button
                          key={t.id}
                          onClick={() => handleSelectTemplate(t.id)}
                          style={{
                            padding: '6px 10px', borderRadius: '8px',
                            border: selectedTemplateId === t.id ? `2px solid ${colors.primary}` : '1px solid #ddd',
                            background: selectedTemplateId === t.id ? colors.primaryLight : '#f8f9fa',
                            cursor: 'pointer', fontSize: '12px',
                            color: selectedTemplateId === t.id ? colors.primary : colors.text,
                            fontWeight: selectedTemplateId === t.id ? '600' : '400',
                          }}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message text */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '6px', fontSize: '13px', color: colors.text }}>
                      תוכן ההודעה:
                    </div>
                    <textarea
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder="כתבי את ההודעה כאן..."
                      style={{
                        width: '100%', minHeight: '120px', padding: '12px',
                        borderRadius: '10px',
                        border: selectedTemplateId ? `2px solid ${colors.green}` : `2px solid ${colors.border}`,
                        fontSize: '14px', resize: 'vertical',
                        boxSizing: 'border-box', lineHeight: 1.6,
                        background: selectedTemplateId ? '#f1f8e9' : 'white',
                      }}
                    />
                  </div>

                  {/* Continue button */}
                  <button
                    onClick={() => { if (messageText.trim()) setComposeStep('confirm'); }}
                    disabled={!messageText.trim()}
                    style={{
                      width: '100%', padding: '14px', borderRadius: '12px',
                      border: 'none',
                      background: !messageText.trim()
                        ? '#ccc'
                        : `linear-gradient(135deg, ${colors.primary}, #a78bfa)`,
                      color: 'white', fontSize: '15px', fontWeight: 'bold',
                      cursor: !messageText.trim() ? 'not-allowed' : 'pointer',
                    }}
                  >
                    המשך לאישור
                  </button>
                </div>
              )}

              {/* Step 4: Confirm */}
              {composeStep === 'confirm' && (
                <div>
                  <div style={{
                    padding: '14px', background: '#f8f9fa',
                    borderRadius: '10px', marginBottom: '16px',
                  }}>
                    <div style={{ fontWeight: '600', fontSize: '13px', color: colors.textLight, marginBottom: '6px' }}>
                      נמען:
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '15px', color: colors.text }}>
                      {selectedChild ? `${selectedChild.firstName} ${selectedChild.lastName} (הורים)` : 'כל הורי הגן'}
                    </div>
                  </div>

                  <div style={{
                    padding: '14px', background: '#f8f9fa',
                    borderRadius: '10px', marginBottom: '16px',
                  }}>
                    <div style={{ fontWeight: '600', fontSize: '13px', color: colors.textLight, marginBottom: '6px' }}>
                      תוכן ההודעה:
                    </div>
                    <div style={{ fontSize: '14px', color: colors.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {messageText}
                    </div>
                  </div>

                  {/* SMS toggle */}
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '12px', background: sendToMobile ? colors.greenLight : '#f8f9fa',
                    borderRadius: '10px', marginBottom: '16px', cursor: 'pointer',
                    border: sendToMobile ? `2px solid ${colors.green}` : '2px solid transparent',
                  }}>
                    <input
                      type="checkbox"
                      checked={sendToMobile}
                      onChange={(e) => setSendToMobile(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: colors.green }}
                    />
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>שלח גם SMS לנייד</div>
                      <div style={{ fontSize: '11px', color: colors.textLight }}>ההודעה תישלח גם כ-SMS לטלפון</div>
                    </div>
                  </label>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setComposeStep('compose')}
                      style={{
                        flex: 1, padding: '14px', borderRadius: '12px',
                        border: `2px solid ${colors.border}`, background: 'white',
                        fontSize: '14px', cursor: 'pointer', color: colors.text,
                      }}
                    >
                      חזרה לעריכה
                    </button>
                    <button
                      onClick={handleSend}
                      disabled={sending}
                      style={{
                        flex: 2, padding: '14px', borderRadius: '12px',
                        border: 'none',
                        background: sending ? '#ccc' : `linear-gradient(135deg, ${colors.green}, #4ade80)`,
                        color: 'white', fontSize: '15px', fontWeight: 'bold',
                        cursor: sending ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      }}
                    >
                      {sending ? (
                        <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> שולח...</>
                      ) : (
                        <><Send size={16} /> אשר ושלח</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Templates manager */}
              {composeStep === 'templates' && (
                <div>
                  {/* Template form */}
                  <div style={{
                    padding: '14px', background: '#f8f9fa',
                    borderRadius: '12px', marginBottom: '16px',
                  }}>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '12px' }}>
                      {editingTemplate ? 'עריכת תבנית' : 'יצירת תבנית חדשה'}
                    </div>

                    {/* Type selector */}
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                      <button
                        onClick={() => setTemplateType('child')}
                        style={{
                          flex: 1, padding: '8px', borderRadius: '8px',
                          border: templateType === 'child' ? '2px solid #4CAF50' : '2px solid #e0e0e0',
                          background: templateType === 'child' ? '#e8f5e9' : 'white',
                          cursor: 'pointer', fontSize: '12px',
                        }}
                      >
                        לילד/ה ספציפי/ת
                      </button>
                      <button
                        onClick={() => setTemplateType('general')}
                        style={{
                          flex: 1, padding: '8px', borderRadius: '8px',
                          border: templateType === 'general' ? '2px solid #2196F3' : '2px solid #e0e0e0',
                          background: templateType === 'general' ? '#e3f2fd' : 'white',
                          cursor: 'pointer', fontSize: '12px',
                        }}
                      >
                        כללית לכל הגן
                      </button>
                    </div>

                    <input
                      type="text"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="שם התבנית"
                      style={{
                        width: '100%', padding: '10px', borderRadius: '8px',
                        border: `2px solid ${colors.border}`, fontSize: '14px',
                        boxSizing: 'border-box', marginBottom: '8px',
                      }}
                    />

                    <textarea
                      value={templateContent}
                      onChange={(e) => setTemplateContent(e.target.value)}
                      placeholder={templateType === 'child'
                        ? 'תוכן ההודעה... (השתמשי ב-{childName} לשם הילד/ה)'
                        : 'תוכן ההודעה...'
                      }
                      style={{
                        width: '100%', minHeight: '80px', padding: '10px',
                        borderRadius: '8px', border: `2px solid ${colors.border}`,
                        fontSize: '14px', resize: 'vertical',
                        boxSizing: 'border-box', marginBottom: '8px',
                      }}
                    />

                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={handleSaveTemplate}
                        disabled={!templateName.trim() || !templateContent.trim()}
                        style={{
                          flex: 1, padding: '10px', borderRadius: '8px',
                          border: 'none',
                          background: (!templateName.trim() || !templateContent.trim())
                            ? '#ccc' : 'linear-gradient(135deg, #4CAF50, #8BC34A)',
                          color: 'white', fontSize: '13px', fontWeight: 'bold',
                          cursor: (!templateName.trim() || !templateContent.trim()) ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {editingTemplate ? 'עדכן' : 'שמור תבנית'}
                      </button>
                      {editingTemplate && (
                        <button
                          onClick={resetTemplateForm}
                          style={{
                            padding: '10px 16px', borderRadius: '8px',
                            border: `1px solid ${colors.border}`, background: 'white',
                            cursor: 'pointer', fontSize: '13px',
                          }}
                        >
                          ביטול
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Template list */}
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#4CAF50', marginBottom: '6px' }}>
                      תבניות לילד/ה:
                    </div>
                    {templates.filter(t => t.type === 'child').map(t => (
                      <div key={t.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 10px', background: editingTemplate?.id === t.id ? '#e3f2fd' : '#f8f9fa',
                        borderRadius: '8px', marginBottom: '4px',
                        border: editingTemplate?.id === t.id ? '2px solid #2196F3' : '1px solid #eee',
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '600', fontSize: '12px' }}>
                            {t.name}
                            {t.isDefault && <span style={{ fontSize: '9px', color: '#999', marginRight: '4px' }}>(ברירת מחדל)</span>}
                          </div>
                          <div style={{ fontSize: '11px', color: colors.textLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t.content}
                          </div>
                        </div>
                        {!t.isDefault && (
                          <div style={{ display: 'flex', gap: '4px', marginRight: '8px' }}>
                            <button onClick={() => handleEditTemplate(t)} style={{ padding: '3px 6px', background: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '10px' }}>ערוך</button>
                            <button onClick={() => deleteTemplate(t.id)} style={{ padding: '3px 6px', background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '10px' }}>מחק</button>
                          </div>
                        )}
                      </div>
                    ))}

                    <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#2196F3', marginTop: '12px', marginBottom: '6px' }}>
                      תבניות כלליות:
                    </div>
                    {templates.filter(t => t.type === 'general').map(t => (
                      <div key={t.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 10px', background: editingTemplate?.id === t.id ? '#e3f2fd' : '#f8f9fa',
                        borderRadius: '8px', marginBottom: '4px',
                        border: editingTemplate?.id === t.id ? '2px solid #2196F3' : '1px solid #eee',
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '600', fontSize: '12px' }}>
                            {t.name}
                            {t.isDefault && <span style={{ fontSize: '9px', color: '#999', marginRight: '4px' }}>(ברירת מחדל)</span>}
                          </div>
                          <div style={{ fontSize: '11px', color: colors.textLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t.content}
                          </div>
                        </div>
                        {!t.isDefault && (
                          <div style={{ display: 'flex', gap: '4px', marginRight: '8px' }}>
                            <button onClick={() => handleEditTemplate(t)} style={{ padding: '3px 6px', background: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '10px' }}>ערוך</button>
                            <button onClick={() => deleteTemplate(t.id)} style={{ padding: '3px 6px', background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '10px' }}>מחק</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '80px', left: '50%', transform: 'translateX(-50%)',
          padding: '12px 24px', borderRadius: '12px',
          background: toast.startsWith('שגיאה') ? '#f44336' : '#4CAF50',
          color: 'white', fontSize: '14px', fontWeight: 'bold',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 2000,
          whiteSpace: 'nowrap',
        }}>
          {toast}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
